import {
	BagPaymentListAddPositionModalData,
	BagPaymentPositionFormValue,
} from '@/bag/bag-payment-list/bag-payment-list.model';
import { BagPaymentListService } from '@/bag/bag-payment-list/bag-payment-list.service';
import { DataBagService } from '@/common/data-bag.service';
import { LoaderService } from '@/common/loader/loader.service';
import { UserData } from '@/reservation/reservation.model';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { BagPaymentListProfileAddComponent } from '../bag-payment-list-profile-add/bag-payment-list-profile-add.component';

@Component({
	selector: 'app-bag-payment-list-add-position-modal',
	templateUrl: './bag-payment-list-add-position-modal.component.html',
	styleUrls: ['./bag-payment-list-add-position-modal.component.scss'],
})
export class BagPaymentListAddPositionModalComponent implements OnInit {
	readonly isEditMode: boolean;
	profileData: UserData[];

	submitted = false;
	filteredUsers: UserData[] = [];
	reservationObj: Array<{ id: number; ownerUser?: { firstName: string; lastName: string } }> = [];

	readonly form;

	private readonly dialogRef = inject(MatDialogRef<BagPaymentListAddPositionModalComponent>);
	private readonly dialogData = inject<BagPaymentListAddPositionModalData>(MAT_DIALOG_DATA);
	private readonly fb = inject(FormBuilder);
	private readonly bag = inject(DataBagService);
	private readonly loader = inject(LoaderService);
	private readonly dialog = inject(MatDialog);
	private readonly bagPaymentListService = inject(BagPaymentListService);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		this.form = this.fb.group({
			tableId: new FormControl<number | string | null>(null, [Validators.required, Validators.minLength(3)]),
			userData: new FormControl<number | string | null>(null, [Validators.required]),
			amount: new FormControl<number | null>(null, [Validators.required]),
			kind: new FormControl<string>('transfer', { nonNullable: true }),
			organization: new FormControl<null>(null),
			bankAccountIncoming: new FormControl<string | null>(null, [Validators.required]),
			invoiceNumber: new FormControl<string | null>(null),
			status: new FormControl<string>('CO', { nonNullable: true }),
			currency: new FormControl<number>(1, { nonNullable: true }),
			payment: new FormControl<Date | string | null>(null, [Validators.required]),
			posting: new FormControl<null>(null),
			table_name: new FormControl<string>('reservation_reservation', { nonNullable: true }),
			note: new FormControl<string>(''),
		});
		this.profileData = [...this.dialogData.profileData];
		this.isEditMode = !!this.dialogData.position;
	}

	ngOnInit(): void {
		if (this.dialogData.position) {
			this.form.patchValue(this.dialogData.position);
		}

		this.form.controls.userData.valueChanges
			.pipe(
				startWith(this.form.controls.userData.value ?? ''),
				debounceTime(400),
				map((value) => this.filterUsers(value)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((filtered) => {
				this.filteredUsers = filtered;
			});

		this.form.controls.tableId.valueChanges
			.pipe(debounceTime(900), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.filterReservation(value);
			});

		this.bagPaymentListService.users.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
			this.profileData = users;
			this.filteredUsers = this.filterUsers(this.form.controls.userData.value ?? '');
		});
	}

	displayUserProfile(item: number | string | null): string {
		const profile = this.profileData.find((profileItem) => profileItem.id === item);
		if (!profile) {
			return '';
		}

		return `${profile.firstName} ${profile.lastName}`;
	}

	displayReservation(item: number | string | null): string {
		const reservation = this.reservationObj.find((reservationItem) => reservationItem.id === item);
		if (!reservation) {
			return item ? String(item) : '';
		}

		return `${reservation.id} | ${reservation.ownerUser?.firstName ?? ''} ${reservation.ownerUser?.lastName ?? ''}`.trim();
	}

	selectOption($event: MatAutocompleteSelectedEvent): void {
		return $event.option.value;
	}

	addPayer(): void {
		this.dialog
			.open(BagPaymentListProfileAddComponent)
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe();
	}

	save(): void {
		this.submitted = true;
		if (!this.form.valid) {
			return;
		}

		this.dialogRef.close(this.form.getRawValue() as BagPaymentPositionFormValue);
	}

	cancel(): void {
		this.dialogRef.close();
	}

	private filterUsers(value: unknown): UserData[] {
		if (value && typeof value === 'string') {
			const filterValue = value.toLowerCase();
			if (filterValue.length < 3) {
				return this.profileData;
			}

			return this.profileData.filter(
				(option) =>
					option.firstName.toLowerCase().includes(filterValue) ||
					option.lastName.toLowerCase().includes(filterValue),
			);
		}

		return this.profileData;
	}

	private filterReservation(value: unknown): void {
		if (!value || typeof value !== 'string') {
			return;
		}

		this.loader.show();
		this.bag.get(`/reservation/list_filter/?q=${value}`, (result: { results?: Array<{ id: number }> }) => {
			this.reservationObj = result.results ?? [];
			this.setOwnerUser();
			this.loader.hide();
		});
	}

	private setOwnerUser(): void {
		const tableId = this.form.controls.tableId.value;
		if (!tableId) {
			return;
		}

		this.bag.get(
			`/reservation/list_filter/?q=${tableId}`,
			(result: { results?: Array<{ ownerUser?: { id?: number | string } }> }) => {
				if (!result.results?.length) {
					return;
				}

				this.form.controls.userData.setValue(result.results[0].ownerUser?.id ?? null);
			},
		);
	}
}
